(() => {
  const root = window.Shopify?.routes?.root || '/';
  const initAnnouncements = (root = document) => {
    root.querySelectorAll('[data-pks-announcement]').forEach(section => {
      if (section.dataset.pksAnnouncementReady === 'true') return;
      const slider = section.querySelector('[data-pks-announcement-slider]');
      const slides = [...(slider?.querySelectorAll('[data-pks-announcement-slide]') || [])];
      if (!slider || slides.length < 2) return;
      section.dataset.pksAnnouncementReady = 'true';
      let activeIndex = Math.max(0, slides.findIndex(slide => slide.classList.contains('active')));
      let transitioning = false;
      let timer;
      const duration = Math.max(3000, Number(slider.dataset.slideDuration || 4000));
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const show = target => {
        if (transitioning || target === activeIndex) return;
        transitioning = true;
        const outgoing = slides[activeIndex];
        const incoming = slides[(target + slides.length) % slides.length];
        incoming.classList.add('entering');
        incoming.offsetHeight;
        outgoing.classList.remove('active');
        outgoing.classList.add('exiting');
        incoming.classList.remove('entering');
        incoming.classList.add('active');
        window.setTimeout(() => {
          outgoing.classList.remove('exiting');
          activeIndex = (target + slides.length) % slides.length;
          transitioning = false;
        }, reducedMotion ? 0 : 500);
      };
      const resetTimer = () => {
        window.clearInterval(timer);
        if (!reducedMotion) timer = window.setInterval(() => {
          if (!document.hidden) show(activeIndex + 1);
        }, duration);
      };
      slider.querySelector('[data-pks-announcement-prev]')?.addEventListener('click', () => { show(activeIndex - 1); resetTimer(); });
      slider.querySelector('[data-pks-announcement-next]')?.addEventListener('click', () => { show(activeIndex + 1); resetTimer(); });
      section.addEventListener('mouseenter', () => window.clearInterval(timer));
      section.addEventListener('mouseleave', resetTimer);
      resetTimer();
    });
  };
  initAnnouncements();
  document.addEventListener('shopify:section:load', event => initAnnouncements(event.target));

  let revealObserver;
  const motionNodes = (selector, scope) => [
    ...(scope.matches?.(selector) ? [scope] : []),
    ...scope.querySelectorAll(selector)
  ];
  const initMotion = (scope = document) => {
    const reveals = motionNodes('[data-pks-reveal]', scope);
    reveals.forEach(section => section.closest('.pks-shell')?.classList.add('motion-ready'));
    motionNodes('[data-pks-stagger]', scope).forEach(group => {
      [...group.children].forEach((item, index) => {
        item.dataset.pksStaggerItem = '';
        item.style.setProperty('--pks-stagger-index', Math.min(index, 7));
      });
    });
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      reveals.forEach(section => section.classList.add('is-visible'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      }, {rootMargin: '0px 0px -8% 0px', threshold: 0.08});
    }
    reveals.forEach(section => {
      if (section.dataset.pksRevealReady === 'true') return;
      section.dataset.pksRevealReady = 'true';
      revealObserver.observe(section);
    });
  };
  initMotion();
  document.addEventListener('shopify:section:load', event => initMotion(event.target));

  const melbourneTimeParts = new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Melbourne', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
  });
  const nextMelbourneMidnight = () => {
    const parts = Object.fromEntries(melbourneTimeParts.formatToParts(new Date()).map(part => [part.type, part.value]));
    const nextDay = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day) + 1));
    const year = nextDay.getUTCFullYear();
    const month = nextDay.getUTCMonth();
    const day = nextDay.getUTCDate();
    const targetWallTime = Date.UTC(year, month, day, 0, 0, 0);
    let target = targetWallTime;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const local = Object.fromEntries(melbourneTimeParts.formatToParts(new Date(target)).map(part => [part.type, part.value]));
      const localAsUtc = Date.UTC(Number(local.year), Number(local.month) - 1, Number(local.day), Number(local.hour), Number(local.minute), Number(local.second));
      target += targetWallTime - localAsUtc;
    }
    return target;
  };
  let dealCountdownTimer;
  let dealCountdownTarget;
  const updateDealCountdowns = () => {
    const nodes = document.querySelectorAll('[data-pks-deal-countdown]');
    if (!nodes.length) return;
    const now = Date.now();
    if (!dealCountdownTarget || now >= dealCountdownTarget) dealCountdownTarget = nextMelbourneMidnight();
    const secondsLeft = Math.max(0, Math.floor((dealCountdownTarget - now) / 1000));
    const hours = String(Math.floor(secondsLeft / 3600)).padStart(2, '0');
    const minutes = String(Math.floor((secondsLeft % 3600) / 60)).padStart(2, '0');
    const seconds = String(secondsLeft % 60).padStart(2, '0');
    nodes.forEach(node => {
      const separator = node.dataset.pksDealCountdown === 'compact' ? ':' : ' : ';
      node.textContent = [hours, minutes, seconds].join(separator);
    });
    if (!dealCountdownTimer) dealCountdownTimer = window.setInterval(updateDealCountdowns, 1000);
  };
  updateDealCountdowns();
  document.addEventListener('visibilitychange', () => { if (!document.hidden) updateDealCountdowns(); });
  document.addEventListener('shopify:section:load', updateDealCountdowns);

  const initNativeProductAccordions = (scope = document) => {
    const accordions = motionNodes('[data-pks-native-accordion]', scope);
    accordions.forEach(accordion => {
      if (accordion.dataset.pksAccordionReady === 'true') return;
      accordion.dataset.pksAccordionReady = 'true';
      const panel = accordion.querySelector('.t-acc-panel');
      const sync = () => {
        accordion.dataset.open = String(accordion.open);
        if (!panel) return;
        panel.inert = !accordion.open;
        panel.setAttribute('aria-hidden', String(!accordion.open));
      };
      accordion.addEventListener('toggle', sync);
      sync();
    });
  };
  initNativeProductAccordions();
  document.addEventListener('shopify:section:load', event => initNativeProductAccordions(event.target));

  const menuCloseDuration = element => {
    const value = getComputedStyle(element).transitionDuration.split(',')[0].trim();
    const amount = Number.parseFloat(value) || 0.15;
    return value.endsWith('ms') ? amount : amount * 1000;
  };
  let mobileMenuTimer;
  const mobileMenu = document.getElementById('MobileMenu');
  const mobileMenuToggle = document.querySelector('[data-menu-toggle]');
  const setMobileMenuOpen = (open, returnFocus = false) => {
    if (!mobileMenu || !mobileMenuToggle) return;
    window.clearTimeout(mobileMenuTimer);
    mobileMenuToggle.setAttribute('aria-expanded', String(open));
    if (open) {
      mobileMenu.hidden = false;
      mobileMenu.inert = false;
      mobileMenu.setAttribute('aria-hidden', 'false');
      mobileMenu.classList.remove('is-closing');
      mobileMenu.offsetHeight;
      mobileMenu.classList.add('is-open');
      mobileMenu.querySelector('input,a')?.focus();
      return;
    }
    mobileMenu.inert = true;
    mobileMenu.setAttribute('aria-hidden', 'true');
    mobileMenu.classList.remove('is-open');
    mobileMenu.classList.add('is-closing');
    mobileMenuTimer = window.setTimeout(() => {
      if (mobileMenuToggle.getAttribute('aria-expanded') === 'true') return;
      mobileMenu.hidden = true;
      mobileMenu.classList.remove('is-closing');
      if (returnFocus) mobileMenuToggle.focus();
    }, menuCloseDuration(mobileMenu));
  };
  const closeDesktopMenu = details => {
    if (!details?.open) return;
    const menu = details.querySelector('.nav-dropdown.t-dropdown');
    const summary = details.querySelector('summary');
    if (!menu) { details.open = false; return; }
    menu.classList.remove('is-open');
    menu.classList.add('is-closing');
    summary?.setAttribute('aria-expanded', 'false');
    window.setTimeout(() => {
      if (!menu.classList.contains('is-closing')) return;
      details.open = false;
      menu.classList.remove('is-closing');
    }, menuCloseDuration(menu));
  };

  const status = document.createElement('div');
  status.setAttribute('role','status');
  status.setAttribute('aria-live','polite');
  status.className = 'visually-hidden';
  document.body.append(status);

  document.addEventListener('click', event => {
    const productImageDot = event.target.closest('[data-product-image-index]');
    if (productImageDot) {
      const card = productImageDot.closest('.product-card');
      const image = card?.querySelector('.product-card-image');
      if (image) {
        const token = `${Date.now()}-${Math.random()}`;
        image.dataset.pksImageSwap = token;
        image.classList.add('t-image-swap', 'is-changing');
        const finish = () => {
          if (image.dataset.pksImageSwap !== token) return;
          image.classList.remove('is-changing');
        };
        image.addEventListener('load', finish, {once:true});
        image.addEventListener('error', finish, {once:true});
        requestAnimationFrame(() => {
          if (image.dataset.pksImageSwap !== token) return;
          image.src = productImageDot.dataset.imageSrc;
          image.srcset = productImageDot.dataset.imageSrcset || '';
          image.sizes = '(max-width: 749px) 72vw, 240px';
          if (image.complete) requestAnimationFrame(finish);
        });
        image.alt = productImageDot.dataset.imageAlt || image.alt;
        card.querySelectorAll('[data-product-image-index]').forEach(dot => {
          const active = dot === productImageDot;
          dot.classList.toggle('active', active);
          dot.setAttribute('aria-pressed', String(active));
        });
      }
      return;
    }

    const accordionToggle = event.target.closest('[data-pks-accordion-toggle]');
    if (accordionToggle) {
      const accordion = accordionToggle.closest('[data-pks-accordion]');
      const panel = accordion?.querySelector('.t-acc-panel');
      const opening = accordion?.dataset.open !== 'true';
      if (accordion && panel) {
        accordion.dataset.open = String(opening);
        accordionToggle.setAttribute('aria-expanded', String(opening));
        panel.inert = !opening;
        panel.setAttribute('aria-hidden', String(!opening));
      }
      return;
    }

    const desktopSummary = event.target.closest('.site-header .desktop-nav details > summary');
    if (desktopSummary) {
      event.preventDefault();
      const details = desktopSummary.parentElement;
      if (details.open) closeDesktopMenu(details);
      else {
        document.querySelectorAll('.site-header .desktop-nav details[open]').forEach(openDetails => closeDesktopMenu(openDetails));
        details.open = true;
        desktopSummary.setAttribute('aria-expanded', 'true');
        const menu = details.querySelector('.nav-dropdown.t-dropdown');
        if (menu) {
          menu.classList.remove('is-closing');
          menu.offsetHeight;
          menu.classList.add('is-open');
        }
      }
      return;
    }

    if (!event.target.closest('.site-header .desktop-nav')) {
      document.querySelectorAll('.site-header .desktop-nav details[open]').forEach(closeDesktopMenu);
    }

    const toggle = event.target.closest('[data-menu-toggle]');
    if (toggle) {
      setMobileMenuOpen(toggle.getAttribute('aria-expanded') !== 'true');
      return;
    }

    const reviewControl = event.target.closest('[data-review-prev],[data-review-next]');
    if (reviewControl) {
      const track = reviewControl.closest('.pks-testimonials')?.querySelector('[data-review-track]');
      const card = track?.querySelector('.testimonial-card');
      if (!track) return;
      const gap = Number.parseFloat(getComputedStyle(track).gap) || 0;
      const distance = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
      const direction = reviewControl.hasAttribute('data-review-next') ? 1 : -1;
      track.scrollBy({left: direction * distance, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      return;
    }

    const carouselControl = event.target.closest('[data-carousel-prev],[data-carousel-next]');
    if (!carouselControl) return;
    const shelf = carouselControl.closest('.product-shelf');
    const track = shelf?.querySelector('[data-carousel-track]');
    if (!track) return;
    const card = track.querySelector('.product-card');
    const gap = Number.parseFloat(getComputedStyle(track).gap) || 0;
    const distance = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    const direction = carouselControl.hasAttribute('data-carousel-next') ? 1 : -1;
    const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    track.scrollBy({left: direction * distance, behavior});
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    if (mobileMenu && !mobileMenu.hidden) setMobileMenuOpen(false, true);
    document.querySelectorAll('.site-header .desktop-nav details[open]').forEach(closeDesktopMenu);
  });

  // Native cart sections retain Elixir and AOV compatibility. Shopify validates stock.
  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-pks-product-form]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[type=submit]');
    const error = form.querySelector('[data-form-error]');
    const originalText = button.textContent;
    if (button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy','true');
    button.textContent = 'Adding…';
    if (error) error.hidden = true;
    const body = new FormData(form);
    const cart = document.querySelector('cart-drawer');
    const sectionIds = cart?.getSectionsToRender?.().map(section=>section.id);
    if (sectionIds?.length) {body.append('sections',sectionIds.join(','));body.append('sections_url',location.pathname);cart.setActiveElement?.(button);}
    try {
      const response = await fetch(`${root}cart/add.js`,{method:'POST',headers:{'X-Requested-With':'XMLHttpRequest'},body});
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || 'This product could not be added. Please try again.');
      status.textContent = 'Added to your cart.';
      if (cart && data.sections && Object.values(data.sections).every(Boolean)) {cart.classList.remove('is-empty');cart.renderContents(data);}
      else {location.assign(`${root}cart`); return;}
      const fresh = await fetch(`${root}cart.js`).then(r=>r.json());
      document.querySelectorAll('[data-cart-count]').forEach(el=>el.textContent=fresh.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated',{detail:fresh}));
    } catch (failure) {
      if (error) {error.textContent=failure.message;error.hidden=false;} else status.textContent=failure.message;
    } finally {button.disabled=false;button.removeAttribute('aria-busy');button.textContent=originalText;}
  });

  document.addEventListener('change', event => {
    if (event.target.matches('[data-catalogue-sort]')) {
      const url = new URL(location.href);url.searchParams.set('sort_by',event.target.value);url.searchParams.delete('page');location.assign(url);
    }
  });

  const initStickyProductButton = (scope = document) => {
    const bar = scope.querySelector?.('[data-pks-sticky-atc]') || document.querySelector('[data-pks-sticky-atc]');
    if (!bar || bar.dataset.pksStickyReady === 'true') return;
    const productSection = document.querySelector('.shop-product-section');
    const sourceButton = productSection?.querySelector('.shop-add-to-cart-button');
    const stickyButton = bar.querySelector('[data-pks-sticky-add]');
    if (!productSection || !sourceButton || !stickyButton) return;
    bar.dataset.pksStickyReady = 'true';

    const setVisible = visible => {
      const show = !visible && window.scrollY > 100;
      bar.dataset.visible = String(show);
      bar.setAttribute('aria-hidden', String(!show));
      bar.inert = !show;
    };
    const syncButton = () => {
      const busy = sourceButton.getAttribute('aria-busy') === 'true';
      stickyButton.disabled = sourceButton.disabled || busy;
      stickyButton.textContent = busy ? 'Adding…' : sourceButton.disabled ? 'Sold out' : 'Shop now';
      const priceSource = productSection.querySelector('.shop-product-price-block');
      const priceTarget = bar.querySelector('[data-pks-sticky-price]');
      if (priceSource && priceTarget && priceSource.textContent.trim()) priceTarget.textContent = priceSource.textContent.trim();
    };
    stickyButton.addEventListener('click', () => {
      if (!sourceButton.disabled) sourceButton.click();
    });
    syncButton();
    new MutationObserver(syncButton).observe(productSection, {subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['disabled','aria-busy']});

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(entries => setVisible(entries[0]?.isIntersecting ?? true), {rootMargin:'-76px 0px -8% 0px',threshold:0}).observe(sourceButton);
    } else {
      const updateOnScroll = () => setVisible(sourceButton.getBoundingClientRect().bottom > 76);
      window.addEventListener('scroll', updateOnScroll, {passive:true});
      updateOnScroll();
    }
  };
  initStickyProductButton();
  document.addEventListener('shopify:section:load', event => initStickyProductButton(event.target));
})();
